import { differenceInDays } from 'date-fns';
import { useMemo } from 'react';
import { useAppSelector } from '../../../../app/hooks';
import { StrictDateRange } from '../../../commonUI/DateRangeFilterSelect/types';
import { generateDateRangeFilter } from '../../../commonUI/DateRangeFilterSelect/utils/generateDateRangeFilter';
import { selectRanchSummaryTableDateRange } from '../../dataGridTablesSlice';

/**
 * Custom hook to create a date range for the ranch summary table.
 *
 * This hook uses the current date range selection from the Redux store
 * and calculates the start and end dates based on the selection.
 *
 * @returns {StrictDateRange | null} dateRange - The calculated date range with start and end dates,
 * or null if the date range selection is invalid.
 * @returns {number | undefined} daysSinceNow - the number of days spanned by the date range
 */
const useCreateRanchSummaryTableDateRange = (): {
  dateRange: StrictDateRange | null;
  daysSinceNow?: number;
} => {
  const currDateRangeSelection = useAppSelector(
    selectRanchSummaryTableDateRange
  );

  const dateRange = useMemo(() => {
    return generateDateRangeFilter(currDateRangeSelection);
  }, [currDateRangeSelection]);

  if (dateRange) {
    const daysSinceNow = differenceInDays(dateRange.end, dateRange.start);
    return { dateRange, daysSinceNow };
  }

  return { dateRange: null, daysSinceNow: undefined };
};

export default useCreateRanchSummaryTableDateRange;
